import { useLayoutEffect, useRef, useState } from "react";
import type { Camada, CamadaDeImagem, CamadaDeTexto, Tamanho } from "@foto/shared";
import { cx } from "../../components/ui.js";
import {
  corpoDoTextoEmPx,
  estiloCssDaImagem,
  estiloDaFonteDoTexto,
  layoutDoTexto,
  linhasDaPrevia,
  tracoDoTexto,
  type MedidasDoTexto,
} from "./css-das-camadas.js";
import { imagemDaCamada } from "./caixas.js";
import { figurinhaDe, type FigurinhasDasCamadas } from "./figurinhas-das-camadas.js";
import { useFontesDasCamadas } from "./useFontesDasCamadas.js";

/**
 * As camadas de uma célula desenhadas na tela: o mesmo componente na prévia da folha e no editor.
 *
 * Ocupa a célula inteira (`absolute inset-0`) e recorta o que passa da borda — como o servidor. Não recebe toque:
 * no editor, quem acerta a camada é a conta pura de `@foto/shared` (`camadaNoPonto`), igual em qualquer navegador.
 *
 * Mede a largura da célula (uma vez, e de novo quando ela muda) porque o texto se desenha em pixels: o corpo é
 * `corpo`% da largura da célula, como no servidor.
 *
 * `celula` só dá a proporção (a prévia passa milímetros, o editor passa pixels).
 */
export function CamadasNaCelula({
  camadas,
  figurinhas,
  celula,
  aoMedirTexto,
  modo = "previa",
}: {
  camadas: readonly Camada[];
  figurinhas: FigurinhasDasCamadas | undefined;
  celula: Tamanho;
  /**
   * O editor pede o tamanho do bloco de cada texto, em pixels, para a caixa de seleção e o clique. Sem isto
   * (a prévia da folha) nada é entregue.
   */
  aoMedirTexto?: (id: string, bloco: Tamanho) => void;
  /** No editor, o marcador de imagem indisponível diz o que aconteceu; na prévia, só aponta que há algo. */
  modo?: "previa" | "editor";
}) {
  const versaoDasFontes = useFontesDasCamadas(camadas);
  const raizRef = useRef<HTMLDivElement>(null);
  const [larguraPx, setLarguraPx] = useState(0);

  useLayoutEffect(() => {
    const el = raizRef.current;
    if (!el) return;
    const medir = () => setLarguraPx(el.getBoundingClientRect().width);
    medir();
    const observador = new ResizeObserver(medir);
    observador.observe(el);
    return () => observador.disconnect();
  }, []);

  return (
    <div
      ref={raizRef}
      aria-hidden
      className="pointer-events-none absolute inset-0 select-none overflow-hidden"
    >
      {camadas.map((camada) =>
        camada.tipo === "texto" ? (
          <TextoNaCelula
            key={camada.id}
            camada={camada}
            larguraPx={larguraPx}
            versaoDasFontes={versaoDasFontes}
            {...(aoMedirTexto ? { aoMedir: aoMedirTexto } : {})}
          />
        ) : (
          <ImagemNaCelula
            key={camada.id}
            camada={camada}
            figurinhas={figurinhas}
            celula={celula}
            modo={modo}
          />
        ),
      )}
    </div>
  );
}

function ImagemNaCelula({
  camada,
  figurinhas,
  celula,
  modo,
}: {
  camada: CamadaDeImagem;
  figurinhas: FigurinhasDasCamadas | undefined;
  celula: Tamanho;
  modo: "previa" | "editor";
}) {
  const figurinha = figurinhaDe(figurinhas, camada.figurinhaId);
  const estilo = estiloCssDaImagem(camada, imagemDaCamada(camada, figurinhas), celula);

  // `tela` e não `mini`: a camada se amplia e a folha sai em 300 dpi; o servidor usa o arquivo inteiro, e a tela
  // se aproxima dele quanto puder.
  if (figurinha) {
    return (
      <img
        src={figurinha.urls.tela}
        alt=""
        draggable={false}
        className="max-w-none select-none"
        style={estilo}
      />
    );
  }

  const estado = figurinhas?.get(camada.figurinhaId)?.estado;
  if (estado === "indisponivel" || estado === "erro") {
    return (
      <div
        style={estilo}
        className="flex items-center justify-center overflow-hidden border border-dashed border-descartar bg-black/60 p-1 text-center"
      >
        {modo === "editor" ? (
          <span className="text-[0.6875rem] font-medium leading-tight text-white">
            {estado === "indisponivel" ? "Imagem indisponível" : "Imagem não carregou"}
          </span>
        ) : (
          <span className="text-[0.625rem] leading-none text-white">!</span>
        )}
      </div>
    );
  }
  // Ainda carregando: um quadro que respira, no lugar onde a imagem vai ficar.
  return <div style={estilo} className={cx("animate-pulse rounded-sm bg-white/25")} />;
}

const iguais = (a: readonly number[], b: readonly number[]): boolean =>
  a.length === b.length && a.every((v, i) => v === b[i]);

/**
 * Uma camada de texto, em SVG. O bloco é o do servidor (`layoutDoTexto`): a largura de cada linha vem do próprio
 * navegador (`getComputedTextLength`, com os avanços e o kerning reais da fonte, os mesmos que o `fontkit` lê no
 * servidor), a altura sai de `linhas × corpo × entrelinha`, e a linha de base de cada linha, do ascendente e do
 * descendente da fonte (o `getBBox` de um `<text>` é a caixa ascendente + descendente).
 *
 * São duas passadas no mesmo quadro: a primeira desenha as linhas escondidas só para medi-las; o resultado vai
 * para um estado, e a segunda já as põe no lugar — tudo antes da pintura (`useLayoutEffect`), então nada pisca. A
 * medida se refaz quando o texto, a fonte ou o tamanho mudam e quando uma fonte termina de carregar.
 */
function TextoNaCelula({
  camada,
  larguraPx,
  aoMedir,
  versaoDasFontes,
}: {
  camada: CamadaDeTexto;
  larguraPx: number;
  aoMedir?: (id: string, bloco: Tamanho) => void;
  versaoDasFontes: number;
}) {
  const linhas = linhasDaPrevia(camada);
  const corpoPx = corpoDoTextoEmPx(camada, larguraPx);
  const textos = useRef<(SVGTextElement | null)[]>([]);
  const [medidas, setMedidas] = useState<MedidasDoTexto | null>(null);
  const chaveDoTexto = linhas.join("\n");

  useLayoutEffect(() => {
    if (!(corpoPx > 0) || linhas.length === 0) return;
    try {
      const elementos = textos.current.slice(0, linhas.length);
      const larguras = elementos.map((t) => t?.getComputedTextLength() ?? 0);
      const primeiro = elementos[0];
      if (!primeiro) return;
      const caixa = primeiro.getBBox();
      // O `y` do primeiro `<text>` é a sua linha de base: o topo da caixa fica um ascendente acima dela.
      const base = Number(primeiro.getAttribute("y") ?? 0);
      const ascendente = base - caixa.y;
      const descendente = caixa.height - ascendente;
      setMedidas((antes) =>
        antes &&
        iguais(antes.larguras, larguras) &&
        antes.ascendente === ascendente &&
        antes.descendente === descendente
          ? antes
          : { larguras, ascendente, descendente },
      );
    } catch {
      // Sem como medir (elemento fora da tela, navegador que recusa): o texto fica escondido, e a folha impressa
      // sai de qualquer jeito — é o servidor quem a desenha.
    }
  }, [
    chaveDoTexto,
    corpoPx,
    camada.fonteId,
    camada.peso,
    camada.italico,
    camada.entrelinha,
    versaoDasFontes,
  ]);

  const layout =
    medidas && medidas.larguras.length === linhas.length
      ? layoutDoTexto({
          linhas,
          medidas,
          corpoPx,
          entrelinha: camada.entrelinha,
          alinhamento: camada.alinhamento,
        })
      : null;

  const largura = layout?.largura;
  const altura = layout?.altura;
  useLayoutEffect(() => {
    if (aoMedir && largura !== undefined && altura !== undefined) {
      aoMedir(camada.id, { largura, altura });
    }
  }, [aoMedir, camada.id, largura, altura]);

  if (linhas.length === 0 || !(corpoPx > 0)) return null;
  const traco = tracoDoTexto(camada, corpoPx);

  return (
    <svg
      width={Math.max(1, largura ?? 1)}
      height={Math.max(1, altura ?? 1)}
      style={{
        position: "absolute",
        left: `${camada.x}%`,
        top: `${camada.y}%`,
        // O centro do bloco é o centro da camada, e ela gira em torno dele.
        transform: `translate(-50%, -50%) rotate(${camada.rotacao}deg)`,
        // O contorno passa da caixa do bloco; o SVG não o corta.
        overflow: "visible",
        visibility: layout ? "visible" : "hidden",
        ...estiloDaFonteDoTexto(camada),
        fontSize: corpoPx,
        whiteSpace: "pre",
        // Sem o ajuste de tamanho que o navegador faz em letra pequena: o servidor desenha o contorno puro.
        textRendering: "geometricPrecision",
      }}
    >
      {linhas.map((linha, i) => (
        <text
          key={i}
          ref={(el) => {
            textos.current[i] = el;
          }}
          x={layout?.linhas[i]?.x ?? 0}
          y={layout?.linhas[i]?.y ?? 0}
          xmlSpace="preserve"
          fill={camada.cor}
          {...(traco
            ? {
                stroke: traco.cor,
                strokeWidth: traco.largura,
                // Junção redonda, e o traço por baixo do preenchimento: é o que o servidor desenha.
                strokeLinejoin: "round" as const,
                strokeLinecap: "round" as const,
                paintOrder: "stroke fill",
              }
            : {})}
        >
          {linha}
        </text>
      ))}
    </svg>
  );
}
